/**
 * <s-product-gallery> — Product image slider.
 *
 * The track is a native horizontal scroll-snap strip, so swiping and trackpad
 * scrolling work without JavaScript. This component adds previous/next and
 * dot navigation, and keeps the active dot and arrow states in sync with scroll.
 *
 * Commands (dispatched via commandfor/command on buttons targeting this element's id):
 *   --previous — Scroll to the previous slide.
 *   --next     — Scroll to the next slide.
 *   --go-to    — Scroll to the slide in the source button's data-index.
 *
 * Required child markup:
 *   [data-gallery-track]  — The scroll-snap container; each direct child is one slide.
 *   [data-gallery-prev]   — Optional previous button.
 *   [data-gallery-next]   — Optional next button.
 *   [data-gallery-dot]    — Optional dot buttons with data-index.
 *
 * Optional variant sync (on <s-product-gallery>):
 *   data-follow-variant   — Jump to the slide whose data-media-id matches a checked
 *                           variant input's data-media-id.
 *   data-start-media-id   — Slide to show on load (the selected variant's photo).
 */
class SProductGallery extends HTMLElement {
  #ac;
  #track;
  #frame = 0;
  #fade = null;

  connectedCallback() {
    this.#track = this.querySelector('[data-gallery-track]');
    if (!this.#track) return;

    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };

    this.addEventListener('command', this.#handleCommand, opts);
    this.#track.addEventListener('scroll', this.#scheduleSync, { ...opts, passive: true });
    window.addEventListener('resize', this.#scheduleSync, { ...opts, passive: true });

    if (this.hasAttribute('data-follow-variant')) {
      // Variant inputs live in the product form and are replaced by partial rendering, so delegate from the document.
      document.addEventListener('change', this.#handleVariantChange, opts);
      this.#jumpToMedia(this.dataset.startMediaId, 'instant');
      this.#preload();
    }

    if (this.hasAttribute('data-click-nav')) {
      this.#track.addEventListener('pointermove', this.#handlePointerMove, { ...opts, passive: true });
      this.#track.addEventListener('pointerleave', this.#handlePointerLeave, opts);
      this.#track.addEventListener('click', this.#handleClickNav, { ...opts, capture: true });
      this.#track.addEventListener('touchstart', this.#handleTouchStart, { ...opts, passive: true });
      this.#track.addEventListener('touchend', this.#handleTouchEnd, { ...opts, passive: true });
    }

    this.#sync();
  }

  #handleVariantChange = (event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement) || !input.checked) return;
    if (!input.dataset.mediaId) return;
    const index = this.#slides.findIndex((slide) => slide.dataset.mediaId === input.dataset.mediaId);
    if (index < 0 || index === this.#index) return;
    this.#crossfadeTo(index);
  };

  // Cross-fade: a copy of the current photo sits on top and fades out while the gallery jumps
  // instantly to the variant photo underneath, so there is never a blank frame.
  #crossfadeTo(index) {
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const current = this.#slides[this.#index];
    const target = this.#slides[index];
    target?.querySelectorAll('img').forEach((img) => { img.loading = 'eager'; img.fetchPriority = 'high'; });
    if (reduce || !current || !current.animate) {
      this.#goTo(index, 'instant');
      return;
    }
    this.#fade?.remove();
    const host = this.#track.parentElement || this;
    if (getComputedStyle(host).position === 'static') host.style.position = 'relative';
    const trackRect = this.#track.getBoundingClientRect();
    const hostRect = host.getBoundingClientRect();
    const overlay = document.createElement('div');
    overlay.setAttribute('aria-hidden', 'true');
    overlay.style.cssText = `position:absolute;left:${trackRect.left - hostRect.left}px;top:${trackRect.top - hostRect.top}px;width:${trackRect.width}px;height:${trackRect.height}px;overflow:hidden;pointer-events:none;z-index:5;`;
    const copy = current.cloneNode(true);
    copy.removeAttribute('id');
    copy.style.cssText += ';width:100%;height:100%;margin:0;';
    overlay.append(copy);
    host.append(overlay);
    this.#fade = overlay;
    this.#goTo(index, 'instant');
    const finish = () => { overlay.remove(); if (this.#fade === overlay) this.#fade = null; };
    const run = () => {
      const animation = overlay.animate([{ opacity: 1 }, { opacity: 0 }], { duration: 420, easing: 'cubic-bezier(0.4, 0, 0.2, 1)', fill: 'forwards' });
      animation.onfinish = finish;
      animation.oncancel = finish;
    };
    const img = target?.querySelector('img');
    if (img && !img.complete && img.decode) {
      // Wait (briefly) for the new photo so the fade reveals it rather than an empty frame.
      Promise.race([img.decode().catch(() => {}), new Promise((r) => setTimeout(r, 350))]).then(run);
    } else {
      run();
    }
  }

  // Warm every gallery photo in the background after load so colour switches are instant.
  #preload() {
    const warm = () => this.#slides.forEach((slide) => slide.querySelectorAll('img[loading="lazy"]').forEach((img) => { img.loading = 'eager'; }));
    if ('requestIdleCallback' in window) requestIdleCallback(warm, { timeout: 2500 }); else setTimeout(warm, 1200);
  }


  // Click navigation (main gallery only, opt in with data-click-nav).
  // Desktop: the whole photo is the control. The cursor becomes an arrow pointing to whichever half the mouse is on,
  // and a click moves that way (on the first photo it always points forward, on the last always back).
  // Phones and tablets: swipe as normal; a tap on the outer fifth of either side moves that way, a tap in the middle still zooms.
  #desktopQuery = window.matchMedia('(min-width: 64rem) and (hover: hover) and (pointer: fine)');

  #directionAt(clientX) {
    const count = this.#slides.length;
    const index = this.#index;
    const box = this.#track.getBoundingClientRect();
    const ratio = (clientX - box.left) / (box.width || 1);
    if (this.#desktopQuery.matches) {
      return ratio < 0.5 ? 'prev' : 'next';
    }
    if (ratio <= 0.2) return 'prev';
    if (ratio >= 0.8) return 'next';
    return null;
  }

  // Faint black arrow cursors, same thin style as the old photo arrows, a little bigger.
  static #cursors = {
    next: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='22' height='22' viewBox='0 0 22 22'%3E%3Cpath d='M8 4l7 7-7 7' fill='none' stroke='%2312203F' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E") 11 11, e-resize`,
    prev: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='22' height='22' viewBox='0 0 22 22'%3E%3Cpath d='M14 4l-7 7 7 7' fill='none' stroke='%2312203F' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E") 11 11, w-resize`,
  };

  #setCursor(direction) {
    this.dataset.cursor = direction;
    const cursor = SProductGallery.#cursors[direction] || '';
    this.#track.style.cursor = cursor;
    // The photo buttons have their own zoom cursor, so set it on them too.
    this.#track.querySelectorAll('button').forEach((button) => { button.style.cursor = cursor; });
  }

  #handlePointerMove = (event) => {
    if (event.pointerType !== 'mouse' || !this.#desktopQuery.matches || this.#slides.length < 2) return;
    const direction = this.#directionAt(event.clientX);
    if (this.dataset.cursor !== direction) this.#setCursor(direction);
  };

  #handlePointerLeave = () => {
    delete this.dataset.cursor;
    this.#track.style.cursor = '';
    this.#track.querySelectorAll('button').forEach((button) => { button.style.cursor = ''; });
  };

  // Move one photo, looping round: past the last photo comes the first, before the first comes the last.
  // The loop-round jump cross-fades instead of scrolling back through every photo.
  #step(delta) {
    const count = this.#slides.length;
    if (count < 2) return;
    const index = this.#index;
    const target = (index + delta + count) % count;
    if (Math.abs(target - index) > 1) this.#crossfadeTo(target);
    else this.#goTo(target);
  }

  // Swiping past either end on a touch screen loops round too.
  #touchStartX = null;
  #touchStartIndex = 0;

  #handleTouchStart = (event) => {
    if (event.touches.length !== 1) return;
    this.#touchStartX = event.touches[0].clientX;
    this.#touchStartIndex = this.#index;
  };

  #handleTouchEnd = (event) => {
    if (this.#touchStartX === null) return;
    const dx = event.changedTouches[0].clientX - this.#touchStartX;
    this.#touchStartX = null;
    const count = this.#slides.length;
    if (count < 2 || Math.abs(dx) < 40) return;
    const last = count - 1;
    const rtl = getComputedStyle(this.#track).direction === 'rtl';
    const forward = rtl ? dx > 0 : dx < 0;
    // Only when the swipe started on an end photo and the slider could not move any further.
    setTimeout(() => {
      if (this.#index !== this.#touchStartIndex) return;
      if (forward && this.#touchStartIndex === last) this.#crossfadeTo(0);
      else if (!forward && this.#touchStartIndex === 0) this.#crossfadeTo(last);
    }, 120);
  };

  #handleClickNav = (event) => {
    // Keyboard presses (no pointer position) keep opening the zoom.
    if (this.#slides.length < 2 || event.detail === 0) return;
    const direction = this.#directionAt(event.clientX);
    if (!direction) return;
    // Stop the photo's own click (zoom) and move instead.
    event.preventDefault();
    event.stopPropagation();
    const index = this.#index;
    this.#step(direction === 'prev' ? -1 : 1);
    if (this.#desktopQuery.matches) {
      // Point the cursor the right way for the photo now showing.
      setTimeout(() => {
        const next = this.#directionAt(event.clientX);
        if (next) this.#setCursor(next);
      }, 450);
    }
  };

  #jumpToMedia(mediaId, behavior) {
    if (!mediaId) return;
    const index = this.#slides.findIndex((slide) => slide.dataset.mediaId === mediaId);
    if (index >= 0) this.#goTo(index, behavior);
  }

  disconnectedCallback() {
    this.#ac?.abort();
    cancelAnimationFrame(this.#frame);
  }

  get #slides() {
    return Array.from(this.#track.children);
  }

  get #index() {
    const width = this.#track.clientWidth || 1;
    return Math.round(Math.abs(this.#track.scrollLeft) / width);
  }

  #handleCommand = (event) => {
    const count = this.#slides.length;
    if (count < 2) return;

    if (event.command === '--previous') {
      this.#goTo(Math.max(0, this.#index - 1));
    } else if (event.command === '--next') {
      this.#goTo(Math.min(count - 1, this.#index + 1));
    } else if (event.command === '--go-to') {
      const index = Number(event.source?.dataset.index);
      if (Number.isInteger(index)) this.#goTo(index);
    }
  };

  #goTo(index, behavior) {
    const slide = this.#slides[index];
    if (!slide) return;

    const reduceMotion = behavior === 'instant' || window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const direction = getComputedStyle(this.#track).direction === 'rtl' ? -1 : 1;
    this.#track.scrollTo({
      left: direction * index * this.#track.clientWidth,
      behavior: reduceMotion ? 'auto' : 'smooth',
    });
  }

  #scheduleSync = () => {
    cancelAnimationFrame(this.#frame);
    this.#frame = requestAnimationFrame(() => this.#sync());
  };

  #sync() {
    const count = this.#slides.length;
    const index = Math.min(this.#index, count - 1);

    const prev = this.querySelector('[data-gallery-prev]');
    const next = this.querySelector('[data-gallery-next]');
    if (prev) prev.disabled = index <= 0;
    if (next) next.disabled = index >= count - 1;

    for (const dot of this.querySelectorAll('[data-gallery-dot]')) {
      const active = Number(dot.dataset.index) === index;
      dot.toggleAttribute('aria-current', active);
      if (active) dot.setAttribute('aria-current', 'true');
    }
  }
}

if (!customElements.get('s-product-gallery')) {
  customElements.define('s-product-gallery', SProductGallery);
}
